import { StoreHelper } from 'Helpers';

import { useCurrentProject } from '../../../../Configs/CurrentProjectContext';
import { ProductHelper } from '../../../../Helpers/ProductHelper';
import { GetConfigurationResponse } from '../../../../Models/Projects/Configuration';
import { QuestionContextType, QuestionItem, QuestionStep } from '../../../../Models/Questions/QuestionDto';
import { CompleteHelper } from '../../../../Plugins/FloorPlan/Helpers/CompleteHelper';
import { MapExportHelper } from '../../../../Plugins/FloorPlan/Helpers/MapExport/MapExportHelper';
import { MapHelper } from '../../../../Plugins/FloorPlan/Helpers/MapHelper';
import { IOpening } from '../../../../Plugins/FloorPlan/Models/IOpening';
import { IRoom, RoomType } from '../../../../Plugins/FloorPlan/Models/IRoom';
import { IRoomItem } from '../../../../Plugins/FloorPlan/Models/IRoomItem';
import { ConfigurationApi } from '../../../../Services/Api/ConfigurationApi';
import { useIsVendeur } from '../../../Hooks/Vendeur/useVendeur';
import { useRoomInfosValues } from '../Components/Pannels/Rooms/Controllers/RoomInfosValues';
import { HabitationHelper } from '../Helpers/HabitationHelper';
import { PrepareGeneralStepQuestions, QuestionPrepare } from '../Helpers/QuestionPrepare';
import { LoadingStep, QuoteState } from '../QuoteState';
import { CreateMapItemContextParams, QuestionContextUtils } from '../Utils/QuestionContextUtils';
import { getProduitPrincipal, getStepServiceBaseContext, getStepSyntheseBaseContext } from '../Utils/UpdateQuestions';

type LoadConfigurationParams = { configId: string; questions: Array<QuestionItem> };

type QuoteLoadControllerParams = { updateQuote: (values: Partial<QuoteState>) => void };
export const useQuoteLoadController = ({ updateQuote }: QuoteLoadControllerParams) => {
    const isVendeur = useIsVendeur();
    const currentProject = useCurrentProject();
    const roomInfos = useRoomInfosValues();

    const loadConfiguration = async ({ configId, questions }: LoadConfigurationParams) => {
        updateQuote({ loadingStep: LoadingStep.Configuration });
        const configurationResponse: GetConfigurationResponse = await ConfigurationApi.getConfiguration(configId);
        const { floor_plan_json, reponse_list: reponses, configuration_projet, ...projetStores } = configurationResponse.data;
        currentProject.update({
            ...configuration_projet,
            isDirty: false,
            lastSaveDate: undefined,
            lastAutoSave: false,
        });

        const updateStepsQuestionsParams = {
            questions,
            isVendeur,
            reponses,
            fromLoadingConfiguration: true,
            autoValidQuestionCommentType: true,
        };

        const stepHabitation = QuestionPrepare.createStepQuestions(questions, QuestionStep.StepHabitation);
        const stepServices = QuestionPrepare.createStepQuestions(questions, QuestionStep.StepServices);
        const stepSynthese = QuestionPrepare.createStepQuestions(questions, QuestionStep.StepSynthese);

        const params: Partial<PrepareGeneralStepQuestions> = { isVendeur, reponses, autoValidQuestionCommentType: true, fromLoadingConfiguration: true };
        QuestionPrepare.updateGeneralStepsQuestions({ ...params, contextQuestionList: stepHabitation });

        const { state: exportMap, lockedWallsList, flooringWallStartIndexes={} } = MapExportHelper.fromApiJsonToState(
            floor_plan_json,
            roomInfos
        );

        exportMap.rooms?.forEach((item: IRoom) => {
            //* load questions response and search if produit principal exists
            QuestionPrepare.prepareStepsQuestions(item, {
                global_surface_area: exportMap.global_surface_area,
                isVendeur,
                stepHabitation,
                questions,
                reponses,
                fromLoadingConfiguration: true,
                autoValidQuestionCommentType: true
            });
        });

        updateQuote({ loadingStep: LoadingStep.Map });
        const laizeProps = ProductHelper.ToArrayLaizeProps(exportMap.rooms);
        const map = MapHelper.loadRooms({ rooms: exportMap.rooms!, laizeProps, lockedWallsList, flooringWallStartIndexes });
        updateQuote({ loadingStep: LoadingStep.InitConfiguration });

        map.rooms?.forEach((item: IRoom) => {
            const createContextParams: CreateMapItemContextParams = { global_surface_area: map.global_surface_area };
            QuestionPrepare.prepareStepsQuestions(item, { ...updateStepsQuestionsParams, ...createContextParams, stepHabitation });

            item.openings?.forEach((opening: IOpening) => {
                const room = map.rooms?.find((r) => r.roomId === opening.roomId)!;
                const { questionProduitPrincipal } = getProduitPrincipal(room);
                opening.questionsBaseContext = QuestionContextUtils.createOpeningContext(
                    opening,
                    item,
                    createContextParams
                );
                QuestionPrepare.updateItemStepsQuestions({
                    ...updateStepsQuestionsParams,
                    itemId: opening.openingId!,
                    item: opening,
                    questionProduitPrincipal,
                    contextType: QuestionContextType.Openings,
                    openingType: opening.type,
                });
                opening.completionStatus = CompleteHelper.isOpeningComplete(opening, item.openings!);
            });
            item.roomItems?.forEach((roomItem: IRoomItem) => {
                const room = map.rooms?.find((r) => r.roomId === roomItem.roomId)!;
                const { questionProduitPrincipal } = getProduitPrincipal(room);
                roomItem.questionsBaseContext = QuestionContextUtils.createRoomItemContext(
                    roomItem,
                    item,
                    createContextParams
                );
                QuestionPrepare.updateItemStepsQuestions({
                    ...updateStepsQuestionsParams,
                    itemId: roomItem.roomItemId!,
                    item: roomItem,
                    questionProduitPrincipal,
                    contextType: QuestionContextType.RoomElements,
                    roomItemType: roomItem.type,
                });
                roomItem.completionStatus = CompleteHelper.isRoomItemComplete(roomItem, item.roomItems!);
            });
            item.completionStatus = CompleteHelper.isRoomComplete(item, map.rooms!);
        });

        const habitationQuestionsOpen = !HabitationHelper.isFinished(stepHabitation.questions);
        map.roomsCompleted = CompleteHelper.allComplete(map.rooms);

        const quote: QuoteState = {
            stepHabitation,
            stepServices,
            stepSynthese,
            habitationQuestionsOpen,
            projetStoreLead: projetStores.projet_store_lead ?? StoreHelper.getProjectStoreLead({ stepHabitation }),
            projetStoreDelivery:
                projetStores.projet_store_delivery ?? StoreHelper.getProjectStoreDelivery({ stepHabitation }),
        };

        const optionsInitialContext = getStepServiceBaseContext(map, quote);
        QuestionPrepare.updateGeneralStepsQuestions({
            ...params,
            contextQuestionList: stepServices,
            previousContext: optionsInitialContext,
        });

        const syntheseInitialContext = getStepSyntheseBaseContext(map, quote);
        QuestionPrepare.updateGeneralStepsQuestions({
            ...params,
            contextQuestionList: stepSynthese,
            previousContext: syntheseInitialContext,
        });

        map.type = toMapType(map.rooms);

        return { mapState: map, ...quote };
    };

    return { loadConfiguration };
};


const toMapType = (rooms: IRoom[] = []) => {
    const roomTypes = rooms.map((room) => room.type);

    if (roomTypes.length && roomTypes.some(type => type === RoomType.Sol)) {
        return RoomType.Sol;
    }
    if (roomTypes.length && roomTypes.every(type => type === RoomType.Wall)) {
        return RoomType.Wall;
    }
    if (roomTypes.length && roomTypes.some(type => type === RoomType.Stairs)) {
        return RoomType.Stairs;
    }
    return RoomType.Sol
}